import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Spinner } from '@repo/components';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getCompanyProjectEvents, getCompanyProject, getCompany } from 'src/api/company';
import { getAllTags } from 'src/api/admin/tags';
import { getEventTypes } from 'src/api/admin/types';
import { useConfig } from 'src/hooks/useConfig';
import SubcontractingTab from 'src/parts/tabs/SubcontractingTab';
import { useBreadcrumbLevel, useLocalStorage } from '@repo/components/hooks';
export const Route = createFileRoute('/company_/$groupId/projects/$projectId/events')({
	component: RouteComponent,
});

function RouteComponent() {
	const { groupId, projectId } = Route.useParams();
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const queryClient = useQueryClient();

	const { data: events, isLoading: eventsLoading } = useQuery({
		queryKey: ['company-project-events', groupId, projectId],
		queryFn: () => getCompanyProjectEvents(groupId, projectId),
		enabled: !userLoading,
	});

	const { data: project } = useQuery({
		queryKey: ['company-project', groupId, projectId],
		queryFn: () => getCompanyProject(groupId, projectId),
		enabled: !userLoading,
	});

	const { data: group } = useQuery({
		queryKey: ['company-group', groupId],
		queryFn: () => getCompany(groupId),
		enabled: !userLoading,
	});

	const { data: typesResponse } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes('?noPagination=true'),
	});

	const { data: tags } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
	});

	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const { updateValue: setBackUrl } = useLocalStorage<string>('companyPortalEventsBackUrl');
	useBreadcrumbLevel(3, 'Events');

	if (userLoading || eventsLoading) {
		return <Spinner className="text-[#1E1E1E]" />;
	}

	if (!authenticatedUser || !project) return null;

	const verifyPermission = getVerifyPermission(authenticatedUser.permissions);

	const handleReload = () => {
		queryClient.invalidateQueries({ queryKey: ['company-project-events', groupId, projectId] });
	};

	const addEvent = async (data: unknown) => {
		await request.post(`/api/company-portal/groups/${groupId}/projects/${project.id}/add-event`, data);
	};

	return (
		<SubcontractingTab
			authenticatedUser={authenticatedUser}
			groupId={groupId}
			events={events}
			addEvent={addEvent}
			onReload={handleReload}
			types={typesResponse?.data || []}
			tags={tags}
			usesTags={featureFlags?.USES_TAGS || false}
			permissions={{
				view: verifyPermission(PERMISSIONS.companyEventsView),
				create: group?.legalForm !== 'selfEmployed' && verifyPermission(PERMISSIONS.companyEventsCreate),
				update: verifyPermission(PERMISSIONS.companyEventsEdit),
				archive: false,
			}}
			project={project}
			eventDetailUrl={`/company/${groupId}/events`}
			onEventDetailClick={() => setBackUrl(window.location.href)}
			isCompanyPortalEvent={true}
		/>
	);
}
